/**
 * Utilitários de data/hora para o Pagali Admin.
 *
 * A API devolve ISO com "Z". Dois tipos:
 *   (A) hora de parede de CV marcada com Z (datas escritas por pessoas: eventos,
 *       promoções, agendamentos, validades) → fmtParede*; comparar com nowCv().
 *   (B) instantes reais UTC (created_at, pagamentos, logs...) → fmtDate/fmtDateTime/
 *       fmtTime (hora de Cabo Verde, independente do browser).
 *   - toApiDatetime / toInputValue: datetime-local em hora CV ↔ instante (B).
 */

const CV_TZ = 'Atlantic/Cape_Verde'; // UTC-1

// ─── Display ────────────────────────────────────────────────────────────────

/** Data + hora curta: "06/06/2026, 20:00" */
export function fmtDateTime(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-PT', {
    timeZone: CV_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Só data: "06/06/2026" */
export function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-PT', {
    timeZone: CV_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Só hora: "20:00" */
export function fmtTime(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-PT', {
    timeZone: CV_TZ,
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Data por extenso: "sábado, 6 de junho de 2026" */
export function fmtDateLong(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-PT', {
    timeZone: CV_TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// ─── Hora de parede (A) ───────────────────────────────────────────────────────
// Datas escritas por pessoas (datas de eventos, promoções, agendamentos, validades)
// chegam como hora de CV marcada com "Z": mostrar tal e qual, em UTC.

/** Data + hora de parede: "06/06/2026, 20:00" */
export function fmtParedeDateTime(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-PT', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Só data de parede: "06/06/2026" */
export function fmtParedeDate(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-PT', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Só hora de parede: "20:00" */
export function fmtParedeTime(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('pt-PT', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** «Agora» em hora de parede de CV, para comparar com datas (A). */
export function nowCv(): Date {
  return new Date(Date.now() - 3600e3);
}

/** Dia de hoje em CV: "YYYY-MM-DD". */
export function todayCv(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: CV_TZ });
}

/** Dia CV de um instante (B): "YYYY-MM-DD". */
export function dayCv(d: string | Date): string {
  return new Date(d).toLocaleDateString('en-CA', { timeZone: CV_TZ });
}

// ─── Input ↔ API (instantes B) ───────────────────────────────────────────────

/**
 * Converte o valor de um campo datetime-local (ex: "2026-06-06T20:00"),
 * introduzido em hora CV, para ISO UTC. Não depende do fuso do browser.
 * Cabo Verde = UTC-1, portanto 20h00 CV = 21h00 UTC.
 */
export function toApiDatetime(localInput: string): string {
  if (!localInput) return '';
  return new Date(Date.parse(localInput.slice(0, 16) + ':00Z') + 3600e3).toISOString();
}

/**
 * Converte um instante UTC vindo da API para "YYYY-MM-DDTHH:mm" em hora CV,
 * para pré-preencher campos datetime-local. Não depende do fuso do browser.
 */
export function toInputValue(apiDate: string | null | undefined): string {
  if (!apiDate) return '';
  const t = new Date(apiDate).getTime();
  if (isNaN(t)) return '';
  return new Date(t - 3600e3).toISOString().slice(0, 16);
}

// ─── Aliases curtos (compatibilidade com código existente) ───────────────────

/** @deprecated Usar fmtDateTime */
export const formatDate = fmtDateTime;
